import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscribeToCustomers, refreshCustomers } from '../utils/storage';
import LoadingScreen from './LoadingScreen';
import { EmptyState } from './templates';
import './EquipmentPage.css';   // reuse the equipment header / last-sync / refresh styling
import './CustomersPage.css';

// Client ID = the client_code with everything up to and including the first
// "-" stripped, e.g. "26-0047" → "0047". Blank codes stay blank.
export function stripClientId(code) {
  if (!code) return '';
  const i = code.indexOf('-');
  return i === -1 ? code : code.slice(i + 1);
}

export default function CustomersPage() {
  const navigate = useNavigate();
  const [items,   setItems]   = useState([]);
  const [meta,    setMeta]    = useState({ updated_at: null, stale: false, error: null });
  const [loading, setLoading] = useState(true);
  const [pageReady, setPageReady] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const applyRes = useCallback((res) => {
    setItems(Array.isArray(res?.items) ? res.items : []);
    setMeta({
      updated_at: res?.updated_at || null,
      stale: !!res?.stale,
      error: res?.error || null,
    });
  }, []);

  useEffect(() => {
    const unsub = subscribeToCustomers((res) => {
      applyRes(res);
      setLoading(false);
    });
    return () => unsub();
  }, [applyRes]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      applyRes(await refreshCustomers());
    } catch (err) {
      setMeta((m) => ({ ...m, error: err?.message || 'Refresh failed.' }));
    } finally {
      setRefreshing(false);
    }
  }, [applyRes]);

  // Sort by Client ID (stripped code) A→Z.
  const sorted = [...items].sort((a, b) =>
    stripClientId(a.client_code).localeCompare(stripClientId(b.client_code))
  );

  if (!pageReady) {
    return <LoadingScreen isLoading={loading} onDone={() => setPageReady(true)} />;
  }

  return (
    <div className="equipment-page">
      <div className="equipment-page-header">
        <div className="equipment-title-row">
          <h1 className="equipment-page-title">Customers</h1>
          {meta.updated_at && (
            <span className="equipment-last-sync">
              Last synced: {new Date(meta.updated_at).toLocaleString()}
              {meta.stale ? ' (stale)' : ''}
            </span>
          )}
        </div>
        <button
          className="btn btn-secondary equipment-refresh"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {meta.error && (
        <div className="alert alert-danger" style={{ marginBottom: 'var(--space-md)' }}>
          Could not read Clients.json from the drive: {meta.error}
          {items.length > 0 && ' — showing the last cached copy.'}
        </div>
      )}

      {sorted.length === 0 ? (
        <EmptyState icon="🏢"><p>No customers found yet.</p></EmptyState>
      ) : (
        <div className="table-wrapper">
          <table className="ds-table ds-table-fixed cust-table">
            <colgroup>
              <col className="cust-col-code" />
              <col className="cust-col-name" />
              <col className="cust-col-city" />
              <col className="cust-col-country" />
              <col className="cust-col-chevron" />
            </colgroup>
            <thead>
              <tr>
                <th>Client ID</th>
                <th>Company ({sorted.length})</th>
                <th>City</th>
                <th>Country</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr key={c.id} className="cust-row" onClick={() => navigate(`/customers/${c.id}`)}>
                  <td className="cust-code">
                    {!c.muted && !c.assigned_client_id && (
                      <span className="cust-pending-dot" title="Not assigned to a test site" />
                    )}
                    {stripClientId(c.client_code) || '—'}
                  </td>
                  <td className="cust-name">{c.company_name || '—'}</td>
                  <td>{c.city || '—'}</td>
                  <td>{c.country || '—'}</td>
                  <td className="cust-chevron-cell">›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
